import React, { useState } from 'react';

const MatchGrid = ({ matches, onSelectMatch, onDeleteMatch }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const getMatchWinner = (match) => {
    if (match.status !== 'completed') {
      return 'N/A';
    }
    if (match.my_sets_won > match.opponent_sets_won) {
      return 'Me';
    } else if (match.opponent_sets_won > match.my_sets_won) {
      return match.opponent_name;
    }
    return 'Draw'; // Should ideally not happen in tennis, but as a fallback
  };

  const getFormattedScore = (match) => {
    if (match.match_scores_json) {
      try {
        const scores = JSON.parse(match.match_scores_json);
        return scores.map(setScore => `${setScore.myGames}-${setScore.opponentGames}`).join(', ');
      } catch (e) {
        console.error("Error parsing match_scores_json:", e);
        return match.score || 'N/A'; // Fallback to old score or N/A
      }
    }
    return match.score || 'N/A';
  };

  const filteredMatches = matches.filter(match => {
    const opponentName = match.opponent_name ? match.opponent_name.toLowerCase() : '';
    const matchDate = new Date(match.match_date).toLocaleDateString().toLowerCase();
    const status = match.status.toLowerCase();
    const score = getFormattedScore(match).toLowerCase(); // Use new formatted score for search
    const winner = getMatchWinner(match).toLowerCase(); // Use new winner for search

    return opponentName.includes(searchTerm.toLowerCase()) ||
           matchDate.includes(searchTerm.toLowerCase()) ||
           status.includes(searchTerm.toLowerCase()) ||
           score.includes(searchTerm.toLowerCase()) ||
           winner.includes(searchTerm.toLowerCase());
  });

  return (
    <div className="bg-white p-4 rounded-lg shadow-md">
      <div className="flex justify-between items-center mb-4">
        <input
          type="text"
          placeholder="Search matches..."
          className="p-2 border border-gray-300 rounded-md w-1/2"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="overflow-x-auto hidden sm:block"> {/* Hidden on small screens, block on larger */}
        <table className="min-w-full bg-white">
          <thead>
            <tr>
              <th className="py-2 px-4 border-b text-left">Opponent</th>
              <th className="py-2 px-4 border-b text-left">Date</th>
              <th className="py-2 px-4 border-b text-left">Score</th>
              <th className="py-2 px-4 border-b text-left">Status</th>
              <th className="py-2 px-4 border-b text-left">Winner</th>
              <th className="py-2 px-4 border-b text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredMatches.map(match => (
              <tr key={match.match_id} className="hover:bg-gray-100">
                <td className="py-2 px-4 border-b cursor-pointer" onClick={() => onSelectMatch(match)}>{match.opponent_name}</td>
                <td className="py-2 px-4 border-b cursor-pointer" onClick={() => onSelectMatch(match)}>{new Date(match.match_date).toLocaleDateString()}</td>
                <td className="py-2 px-4 border-b cursor-pointer" onClick={() => onSelectMatch(match)}>{getFormattedScore(match)}</td>
                <td className="py-2 px-4 border-b cursor-pointer" onClick={() => onSelectMatch(match)}>
                  <span className={`font-medium ${match.status === 'completed' ? 'text-green-600' : 'text-yellow-600'}`}>
                    {match.status === 'completed' ? 'Completed' : 'In Progress'}
                  </span>
                </td>
                <td className="py-2 px-4 border-b cursor-pointer" onClick={() => onSelectMatch(match)}>{getMatchWinner(match)}</td>
                <td className="py-2 px-4 border-b">
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent row click from triggering
                      onDeleteMatch(match);
                    }}
                    className="text-red-600 hover:text-red-800"
                    title="Delete Match"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 000-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm6 0a1 1 0 11-2 0v6a1 1 0 112 0V8z" clipRule="evenodd" />
                    </svg>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile View */}
      <div className="sm:hidden"> {/* Visible only on small screens */}
        {filteredMatches.length === 0 ? (
          <p className="text-center text-gray-600">No matches found.</p>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {filteredMatches.map(match => (
              <div key={match.match_id} className="bg-gray-50 p-4 rounded-lg shadow-md border border-gray-200">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <p className="text-sm text-gray-500">Opponent:</p>
                    <p className="font-semibold text-lg cursor-pointer" onClick={() => onSelectMatch(match)}>{match.opponent_name}</p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteMatch(match);
                    }}
                    className="text-red-600 hover:text-red-800 ml-4"
                    title="Delete Match"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 000-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm6 0a1 1 0 11-2 0v6a1 1 0 112 0V8z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-gray-500">Date:</p>
                    <p className="font-medium cursor-pointer" onClick={() => onSelectMatch(match)}>{new Date(match.match_date).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Score:</p>
                    <p className="font-medium cursor-pointer" onClick={() => onSelectMatch(match)}>{getFormattedScore(match)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Status:</p>
                    <p className={`font-medium ${match.status === 'completed' ? 'text-green-600' : 'text-yellow-600'} cursor-pointer`} onClick={() => onSelectMatch(match)}>
                      {match.status === 'completed' ? 'Completed' : 'In Progress'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Winner:</p>
                    <p className="font-medium cursor-pointer" onClick={() => onSelectMatch(match)}>{getMatchWinner(match)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MatchGrid;


